import { MoveRight } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Button } from "../../../../components/ui/button";
import { useMobileMenuStore } from "../../../../store/useMobileMenuStore";
import { useLoginModalStore } from "../../../../store/useLoginModalStore";
import { FadeIn } from "../../../../components/animations/FadeIn";
import { HoverScale } from "../../../../components/animations/HoverScale";
import { LoginModal } from "../../../../components/LoginModal/LoginModal";
import { GoogleLoginUI } from "../../../../components/LoginModal/GoogleLoginUI";

export const MediaShowcaseSection = (): JSX.Element => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isMenuOpen, toggleMenu } = useMobileMenuStore();
  const { isOpen, openModal, closeModal } = useLoginModalStore();
  const navItemsRight = ["Features", "Pricing", "Contact"];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: offset, behavior: "smooth" });
    }
  };

  return (
    <section className="relative w-full overflow-hidden">
      {/* Navigation Bar */}
      <nav
        className={`fixed top-0 left-0 w-full py-4 transition-all duration-300 z-50 ${
          isScrolled
            ? "bg-black/20 backdrop-blur-md border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between max-w-[1308px] mx-auto px-4 md:px-6">
          {/* Logo */}
          <div className="flex items-center gap-[15px]">
            <span className="font-panchang font-bold text-white text-[22px]">
              Safora
            </span>
          </div>

          {/* Hamburger Icon (Mobile) */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-5">
              <span
                className={`absolute w-full h-0.5 bg-white transform transition duration-300 ${
                  isMenuOpen ? "rotate-45 top-2" : "top-0"
                }`}
              />
              <span
                className={`absolute w-full h-0.5 bg-white transition duration-300 ${
                  isMenuOpen ? "opacity-0" : "top-2 opacity-100"
                }`}
              />
              <span
                className={`absolute w-full h-0.5 bg-white transform transition duration-300 ${
                  isMenuOpen ? "-rotate-45 top-2" : "top-4"
                }`}
              />
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-6">
            {navItemsRight.map((item, index) => (
              <HoverScale key={index} scale={1.1}>
                <a
                  href={`#${item.toLowerCase()}`}
                  onClick={(e) => handleScroll(e, item.toLowerCase())}
                  className="font-satoshi text-white/80 hover:text-white text-sm transition"
                >
                  {item}
                </a>
              </HoverScale>
            ))}
            <HoverScale>
              <Button 
                onClick={openModal}
                className="bg-white text-gray-900 rounded-[55px] px-[34px] py-3.5 font-satoshi font-bold text-base hover:bg-gray-100"
              >
                Get started
              </Button>
            </HoverScale>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <LoginModal isOpen={isOpen} onClose={closeModal}>
        <GoogleLoginUI />
      </LoginModal>

      {/* Hero Section */}
      <div className="relative w-full overflow-hidden bg-black min-h-screen flex items-center">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('/bent-light-blue-shape-on-black-background-glowing 2.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "left center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {/* Main Content */}
        <div className="relative z-30 max-w-[1308px] mx-auto px-4 md:px-6 w-full">
          <div className="flex flex-col items-start gap-8 md:gap-12">
            {/* Heading and Description */}
            <div className="flex flex-col items-start gap-4 md:gap-7 w-full">
              <FadeIn delay={0.2} direction="up">
                <h1 className="font-satoshi font-bold text-white text-4xl md:text-[64px] leading-tight md:leading-[74px] tracking-[-0.64px] max-w-[600px]">
                  Connect
                  <br />
                  Collaborate
                  <br />
                  Grow
                </h1>
              </FadeIn>
              <FadeIn delay={0.4} direction="up">
                <p className="font-satoshi text-white/60 text-sm md:text-base leading-relaxed md:leading-7 max-w-[500px]">
                  Empower your organization with a unified platform built for
                  seamless communication, smart collaboration, and real progress.
                </p>
              </FadeIn>
            </div>

            {/* CTA Button */}
            <FadeIn delay={0.6} direction="up">
              <HoverScale>
                <Button 
                  onClick={openModal}
                  className="bg-transparent border border-white text-white rounded-[55px] px-6 md:px-[34px] py-3 md:py-3.5 flex items-center gap-2 hover:bg-white/10 text-sm md:text-base"
                >
                  <span className="font-satoshi font-bold">Get Started</span>
                  <MoveRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </Button>
              </HoverScale>
            </FadeIn>
          </div>
        </div>

        {/* Grid & Asphalt Overlays */}
        <div
          className="absolute inset-0 z-20 opacity-30"
          style={{
            backgroundImage: "url('/Grid 1 (1).jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            mixBlendMode: "overlay",
          }}
        />
        <div
          className="absolute inset-0 z-10 opacity-30"
          style={{
            backgroundImage: "url('/asfalt--light--1920x1080.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            mixBlendMode: "overlay",
          }}
        />
      </div>
    </section>
  );
};